import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sv } from 'date-fns/locale';
import config from '../../config/config';
import { addMonths } from 'date-fns';

interface ConvertToInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  quote: any; // Kan specificeras mer exakt om Quote-typ finns
  onSuccess: () => void;
}

const ConvertToInvoiceModal: React.FC<ConvertToInvoiceModalProps> = ({ open, onClose, quote, onSuccess }) => {
  const [form, setForm] = useState({
    deliveryAddress: '',
    deliveryPostalCode: '',
    deliveryCity: '',
    propertyDesignation: '',
    housingAssociationNumber: '',
    apartmentNumber: '',
    workPeriodStart: null,
    workPeriodEnd: null,
    workAddress: '',
    workDescription: '',
    customerPersonalNumber: '',
    dueDate: addMonths(new Date(), 1),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const hasRotOrRut = quote?.items?.some((item: any) => item.isRot || item.isRut);

  React.useEffect(() => {
    if (quote) {
      setForm(f => ({
        ...f,
        deliveryAddress: '',
        deliveryPostalCode: '',
        deliveryCity: '',
        propertyDesignation: '',
        housingAssociationNumber: '',
        apartmentNumber: '',
        workPeriodStart: null,
        workPeriodEnd: null,
        workAddress: '',
        workDescription: '',
        customerPersonalNumber: '',
        dueDate: addMonths(new Date(), 1),
      }));
    }
  }, [quote]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDateChange = (field: string, value: Date | null) => {
    setForm({ ...form, [field]: value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const body = {
        customer_name: quote.customerName,
        customer_email: quote.customerEmail,
        customer_address: quote.customerAddress,
        customer_postal_code: quote.customerPostalCode,
        customer_city: quote.customerCity,
        items: quote.items,
        status: 'unpaid',
        delivery_address: form.deliveryAddress,
        delivery_postal_code: form.deliveryPostalCode,
        delivery_city: form.deliveryCity,
        property_designation: form.propertyDesignation,
        housing_association_number: form.housingAssociationNumber,
        apartment_number: form.apartmentNumber,
        work_period_start: form.workPeriodStart,
        work_period_end: form.workPeriodEnd,
        work_address: form.workAddress,
        work_description: form.workDescription,
        customer_personal_number: form.customerPersonalNumber,
        due_date: form.dueDate ? form.dueDate.toISOString().split('T')[0] : null,
      };
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${config.api.baseUrl}/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Kunde inte skapa faktura');

      // Uppdatera offertens status till 'invoiced'
      const statusRes = await fetch(`${config.api.baseUrl}/quotes/${quote.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'invoiced' })
      });
      if (!statusRes.ok) throw new Error('Kunde inte uppdatera offertens status');

      onSuccess();
    } catch (e) {
      setError('Ett fel uppstod vid skapande av faktura');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Komplettera fakturainformation</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            {hasRotOrRut && <>
              <Grid item xs={12} md={4}>
                <TextField label="Leveransadress" name="deliveryAddress" value={form.deliveryAddress} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField label="Postnummer" name="deliveryPostalCode" value={form.deliveryPostalCode} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField label="Ort" name="deliveryCity" value={form.deliveryCity} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Fastighetsbeteckning" name="propertyDesignation" value={form.propertyDesignation} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField label="Bostadsrättsförening" name="housingAssociationNumber" value={form.housingAssociationNumber} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField label="Lägenhetsnummer" name="apartmentNumber" value={form.apartmentNumber} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField label="Personnummer" name="customerPersonalNumber" value={form.customerPersonalNumber} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={6} md={4}>
                <DatePicker label="Arbetsperiod start" value={form.workPeriodStart} onChange={v => handleDateChange('workPeriodStart', v)} slotProps={{ textField: { fullWidth: true } }} />
              </Grid>
              <Grid item xs={6} md={4}>
                <DatePicker label="Arbetsperiod slut" value={form.workPeriodEnd} onChange={v => handleDateChange('workPeriodEnd', v)} slotProps={{ textField: { fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Arbetsadress" name="workAddress" value={form.workAddress} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Arbetsbeskrivning" name="workDescription" value={form.workDescription} onChange={handleChange} fullWidth multiline rows={2} />
              </Grid>
            </>}
            <Grid item xs={12} md={4}>
              <DatePicker label="Förfallodatum" value={form.dueDate} onChange={v => handleDateChange('dueDate', v)} slotProps={{ textField: { fullWidth: true } }} />
            </Grid>
          </Grid>
          {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Avbryt</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>{loading ? 'Skapar...' : 'Skapa faktura'}</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ConvertToInvoiceModal; 